import { NavLink, Navigate, Link as RouterLink } from "react-router-dom";
import { Form, FormikProps, Formik } from "formik";

import Link from "@mui/material/Link";

import { GoogleLogin } from "@react-oauth/google";

import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LockRounded from "@mui/icons-material/LockRounded";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { google, selectAuth, signInRecaptcha } from "./authSlice";
import { FormikTextField } from "../../components/Layout/FormikTextField";

import AuthSkeleton from "./AuthSkeleton";
import { loginSchema } from "./validation";
import Toast from "../../components/Layout/Toast";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { Helmet } from "react-helmet";
import Divider from "@mui/material/Divider";
import { Stack, useTheme } from "@mui/material";
import { UserCredentials } from "./types/authType";
import { createRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";

interface Values {
  email: string;
  password: string;
  showPassword: boolean;
}

interface SignInProps {
  /** When true, skips the outer Helmet, Container, and Card so it can be
   *  embedded inside another layout (e.g. the landing page). */
  embedded?: boolean;
}

export const SignIn = ({ embedded = false }: SignInProps) => {
  const { loading, error, user } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t: ta } = useTranslation("auth");
  const { t } = useTranslation();
  const [recaptchaUser, setRecaptchaUser] = useState<UserCredentials>({
    email: "",
    password: "",
    captcha: "",
  });

  if (user && user.email) {
    return <Navigate to="/app" />;
  }

  const recaptchaRef = createRef<ReCAPTCHA>();
  const onReCAPTCHAChange = async (captchaCode: string | undefined | null) => {
    if (!captchaCode) return;
    try {
      dispatch(signInRecaptcha({ ...recaptchaUser, captcha: captchaCode }));
    } finally {
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };

  const formContent = (
    <Stack>
      {loading === "pending" ? (
        <AuthSkeleton />
      ) : (
        <Formik
          initialValues={{ email: "", password: "", showPassword: false }}
          validationSchema={loginSchema}
          onSubmit={(values, actions) => {
            actions.setSubmitting(false);
            recaptchaRef.current!.execute();
            setRecaptchaUser({ ...values });
          }}
        >
          {(props: FormikProps<Values>) => (
            <Form>
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                onChange={onReCAPTCHAChange}
              />
              <FormikTextField
                formikKey="email"
                label={ta("email")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                }}
              />
              <FormikTextField
                formikKey="password"
                label={ta("password")}
                type={props.values.showPassword ? "text" : "password"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded />
                    </InputAdornment>
                  ),
                }}
              />
              <Grid container spacing={1}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    textAlign: theme.direction === "ltr" ? "left" : "right",
                  }}
                >
                  <FormControlLabel
                    style={{ marginBottom: 0 }}
                    control={
                      <Switch
                        checked={props.values.showPassword}
                        onChange={props.handleChange("showPassword")}
                        name="showPassword"
                      />
                    }
                    label={ta("showPassword")}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    textAlign: theme.direction === "ltr" ? "right" : "left",
                  }}
                >
                  <Link
                    component={NavLink}
                    underline="none"
                    to="/forgotPassword"
                    color="primary.main"
                  >
                    {ta("forgotPassword")}?
                  </Link>
                </Grid>
              </Grid>
              {error && <Toast severity="error">{error.message}</Toast>}
              <Box>
                <Button
                  sx={{ width: "100%", marginY: "8px" }}
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={!props.isValid}
                >
                  {ta("signin")}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}
      <Divider variant="middle" sx={{ my: 2 }}>
        {ta("or")}
      </Divider>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <GoogleLogin
          onSuccess={(credentialResponse) => {
            dispatch(google(credentialResponse.credential as string));
          }}
          onError={() => {
            console.log("Login Failed");
          }}
          data-text="signin_with"
          theme="filled_blue"
          size="large"
          data-logo_alignment="center"
          data-shape="pill"
          useOneTap
        />
      </Box>
    </Stack>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <>
      <Helmet>
        <title>{`${ta("signin")} | ${t("platform")}`}</title>
      </Helmet>
      <Box
        sx={(theme) => ({
          minHeight: "100vh",
          background: `linear-gradient(150deg, ${
            (theme.palette.primary as any).dark ?? theme.palette.primary.main
          } 0%, ${theme.palette.primary.main} 45%, ${
            theme.palette.secondary.main
          } 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 5,
          px: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 15% 85%, rgba(255,255,255,0.07) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.09) 0%, transparent 45%)",
            pointerEvents: "none",
          },
        })}
      >
        <Stack
          spacing={3}
          alignItems="center"
          sx={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}
        >
          {/* Clickable logo */}
          <Box
            component={RouterLink}
            to="/home"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <img
              src="/cvPlusLogo.png"
              alt="CvPlus"
              style={{ height: 52, objectFit: "contain" }}
            />
          </Box>

          {/* Sign-in card */}
          <Card
            elevation={12}
            sx={{ width: "100%", borderRadius: 3, overflow: "hidden" }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack sx={{ mb: 3, alignItems: "center" }}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {ta("signin")}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Welcome back — sign in to continue
                </Typography>
              </Stack>
              {formContent}
            </CardContent>

            {/* WhatsApp footer strip */}
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                py: 1.5,
                display: "flex",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.02)",
              }}
            >
              <Link
                href="https://wa.me/+966556594373"
                target="_blank"
                underline="none"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: 0.8,
                  transition: "opacity 0.15s ease",
                  "&:hover": { opacity: 1 },
                }}
              >
                <img
                  alt="Chat on WhatsApp"
                  src="/WhatsAppButtonWhiteLarge.svg"
                  width="140px"
                  height="36px"
                />
              </Link>
            </Box>
          </Card>

          {/* Back to home */}
          <Button
            component={RouterLink}
            to="/home"
            startIcon={<ArrowBack fontSize="small" />}
            sx={{
              color: "rgba(255,255,255,0.70)",
              fontWeight: 500,
              fontSize: "0.85rem",
              "&:hover": { color: "rgba(255,255,255,1)" },
            }}
          >
            Back to home
          </Button>
        </Stack>
      </Box>
    </>
  );
};
